import type { StatusTone } from "@/components/ui";
import type {
  ConfigurationAction,
  ConfigurationStatus,
  EmployeeAccessAction,
  EmployeeAccessStatus,
  FeatureFlagAction,
  FeatureFlagStatus,
  MaintenanceModeAction,
  MaintenanceModeStatus,
} from "./types";

export function formatLabel(value: string): string {
  return value.replaceAll("_", " ");
}

/** Summarize opaque JSON without exposing values (secrets / env / PII risk). */
export function summarizeOpaqueObject(
  value: Record<string, unknown> | null | undefined,
  label = "object",
): string {
  if (value == null) return "—";
  const keys = Object.keys(value);
  if (keys.length === 0) return `${label}: empty`;
  return `${label}: ${keys.length} key(s) [${keys.join(", ")}] — values withheld`;
}

export function configStatusTone(status: ConfigurationStatus): StatusTone {
  switch (status) {
    case "draft":
    case "approved":
    case "scheduled":
      return "info";
    case "active":
      return "success";
    case "paused":
      return "warning";
    case "replaced":
    case "retired":
      return "neutral";
    default:
      return "neutral";
  }
}

export function flagStatusTone(status: FeatureFlagStatus): StatusTone {
  switch (status) {
    case "active":
      return "success";
    case "paused":
    case "scheduled":
      return "warning";
    case "disabled":
    case "rolled_back":
      return "danger";
    case "draft":
    case "approved":
      return "info";
    case "retired":
      return "neutral";
    default:
      return "neutral";
  }
}

export function maintenanceStatusTone(
  status: MaintenanceModeStatus,
): StatusTone {
  switch (status) {
    case "active":
      return "danger";
    case "scheduled":
    case "approved":
      return "warning";
    case "proposed":
      return "info";
    case "ended":
    case "cancelled":
      return "neutral";
    default:
      return "neutral";
  }
}

export function accessStatusTone(status: EmployeeAccessStatus): StatusTone {
  switch (status) {
    case "active":
    case "adjusted":
      return "success";
    case "requested":
    case "approved":
      return "info";
    case "suspended":
      return "warning";
    case "revoked":
    case "expired":
      return "neutral";
    default:
      return "neutral";
  }
}

export const CONFIG_ACTIONS_FROM: Record<
  ConfigurationAction,
  readonly ConfigurationStatus[]
> = {
  approve: ["draft"],
  schedule: ["approved"],
  activate: ["approved", "scheduled", "paused"],
  pause: ["active"],
  replace: ["active"],
  retire: ["draft", "approved", "scheduled", "active", "paused", "replaced"],
};

export const FLAG_ACTIONS_FROM: Record<
  FeatureFlagAction,
  readonly FeatureFlagStatus[]
> = {
  approve: ["draft"],
  schedule: ["approved"],
  enable: ["approved", "scheduled", "paused", "disabled"],
  pause: ["active"],
  disable: ["active", "paused", "scheduled"],
  rollback: ["active", "paused", "disabled"],
  retire: [
    "draft",
    "approved",
    "scheduled",
    "active",
    "paused",
    "disabled",
    "rolled_back",
  ],
};

export const MAINT_ACTIONS_FROM: Record<
  MaintenanceModeAction,
  readonly MaintenanceModeStatus[]
> = {
  approve: ["proposed"],
  schedule: ["approved"],
  activate: ["approved", "scheduled"],
  end: ["active"],
  cancel: ["proposed", "approved", "scheduled"],
};

export const ACCESS_ACTIONS_FROM: Record<
  EmployeeAccessAction,
  readonly EmployeeAccessStatus[]
> = {
  approve: ["requested"],
  activate: ["approved", "adjusted"],
  adjust: ["active", "approved"],
  suspend: ["active", "adjusted"],
  revoke: ["requested", "approved", "active", "adjusted", "suspended"],
  expire: ["active", "adjusted", "suspended", "approved"],
  renew: ["expired", "revoked"],
};

export function allowedConfigActions(
  status: ConfigurationStatus,
): ConfigurationAction[] {
  return (Object.keys(CONFIG_ACTIONS_FROM) as ConfigurationAction[]).filter(
    (action) => CONFIG_ACTIONS_FROM[action].includes(status),
  );
}

export function allowedFlagActions(
  status: FeatureFlagStatus,
): FeatureFlagAction[] {
  return (Object.keys(FLAG_ACTIONS_FROM) as FeatureFlagAction[]).filter(
    (action) => FLAG_ACTIONS_FROM[action].includes(status),
  );
}

export function allowedMaintActions(
  status: MaintenanceModeStatus,
): MaintenanceModeAction[] {
  return (Object.keys(MAINT_ACTIONS_FROM) as MaintenanceModeAction[]).filter(
    (action) => MAINT_ACTIONS_FROM[action].includes(status),
  );
}

export function allowedAccessActions(
  status: EmployeeAccessStatus,
): EmployeeAccessAction[] {
  return (Object.keys(ACCESS_ACTIONS_FROM) as EmployeeAccessAction[]).filter(
    (action) => ACCESS_ACTIONS_FROM[action].includes(status),
  );
}

export const ACTION_IMPACT: Record<string, string> = {
  activate:
    "Applies this record as live for its scope. Material marketplace/commission configurations may require Super Admin.",
  pause: "Removes this record from live effect while keeping it recoverable.",
  retire: "Permanently retires this record from the operational set.",
  enable: "Turns the feature flag on for its rollout scope.",
  disable: "Turns the feature flag off for its rollout scope.",
  rollback: "Rolls the feature flag back; availability may change immediately.",
  end: "Ends active maintenance and restores normal operation for the scope.",
  cancel: "Cancels a not-yet-active maintenance window.",
  suspend: "Suspends staff access rights until renewed or revoked.",
  revoke: "Revokes staff access assignment — capabilities end.",
  expire: "Marks the assignment expired per effective window.",
  approve: "Marks this record approved; further activation may still be required.",
  schedule: "Schedules this record; it is not live until activated/enabled.",
  replace:
    "Marks the active configuration replaced (lifecycle only — no value editor).",
  renew: "Renews an expired/revoked assignment to active.",
  adjust: "Adjusts an assignment while keeping it in the access lifecycle.",
};
